import React from 'react';
import {
  Check,
  ChevronsUp,
  Hourglass,
  Inbox,
  Loader,
  UserCheck,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type { EntityChip, FeedState, FeedStateKind, Severity } from '../../data/selectors/feed';
import { sourcesLine } from '../../lib/sourceNames';

/** The Threat Feed's and the campaign screen's small pieces: severity, state and affected entities. */

export const SEVERITY_CLASS: Record<Severity, string> = {
  critical: 'sev-critical',
  high: 'sev-high',
  medium: 'sev-medium',
  low: 'sev-low',
};

/** Every state carries an icon as well as its word (docs/c3/DESIGN.md, colour rule 5). */
export const STATE_ICON: Record<FeedStateKind, LucideIcon> = {
  unassigned: Inbox,
  assigned: UserCheck,
  escalated: ChevronsUp,
  pending_approval: Hourglass,
  pending_execution: Loader,
  closed: Check,
  failed: XCircle,
};

export const StateBadge: React.FC<{ state: FeedState; /** A state the feed does not have, drawn with its own icon. */ icon?: LucideIcon }> = ({ state, icon }) => {
  const count =
    state.kind === 'pending_approval' && state.count != null && state.count > 0 ? ` · ${state.count}` : '';
  const Icon = icon ?? STATE_ICON[state.kind];
  return (
    <span className={`tf-state is-${state.kind}`}>
      <span className="tf-state-text">
        <Icon size={14} strokeWidth={1.5} aria-hidden />
        <span>
          {state.label}
          {count}
        </span>
      </span>
      {state.tag ? <span className="tf-state-tag">{state.tag.charAt(0).toUpperCase() + state.tag.slice(1).toLowerCase()}</span> : null}
    </span>
  );
};

/** The sources of a row as names a person would write ("CrowdStrike Falcon / Okta"), which break at their spaces. */
export const SourceNames: React.FC<{ sources: string[] }> = ({ sources }) => <>{sourcesLine(sources)}</>;

export const ENTITY_CHIP_LIMIT = 3;

export const EntityChips: React.FC<{ entities: EntityChip[]; limit?: number }> = ({
  entities,
  limit = ENTITY_CHIP_LIMIT,
}) => {
  if (entities.length === 0) return <span className="tf-empty">—</span>;
  const shown = entities.slice(0, limit);
  const extra = entities.length - shown.length;
  return (
    <div className="tf-entity-chips">
      {shown.map((chip) => (
        <span key={chip.id} className={`tf-entity-chip is-${chip.kind}`} title={chip.name}>
          {chip.name}
        </span>
      ))}
      {extra > 0 ? (
        <span className="tf-entity-more" title={entities.slice(shown.length).map((chip) => chip.name).join(", ")}>
          +{extra}
        </span>
      ) : null}
    </div>
  );
};
